import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, Paper, Stack, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Drawer, Divider, ToggleButtonGroup, ToggleButton,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import RecyclingIcon from '@mui/icons-material/Recycling';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import ScienceIcon from '@mui/icons-material/Science';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { setupRequestService } from '../services/api';

function fmtDt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function fmtDuration(from, to) {
  if (!from || !to) return null;
  const ms = new Date(to) - new Date(from);
  if (ms < 0) return null;
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m} хв`;
  return `${Math.floor(m / 60)} год ${m % 60} хв`;
}

function InfoRow({ label, value, color }) {
  if (!value && value !== 0) return null;
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" py={0.5}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130 }}>{label}</Typography>
      <Typography variant="caption" fontWeight={700} color={color || 'text.primary'} textAlign="right">{value}</Typography>
    </Stack>
  );
}

function DetailDrawer({ req, onClose, onTake, onComplete, taking }) {
  if (!req) return null;
  const sc = { pending: '#e65100', in_progress: '#1565c0', completed: '#2e7d32' };
  const color = sc[req.status] || '#555';
  const rawMaterials = req.order_raw_materials || [];
  const mixing = req.order_mixing_batch;
  const wastes = req.raw_material_wastes;
  const duration = fmtDuration(req.started_at, req.completed_at);

  return (
    <Drawer anchor="right" open={!!req} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, display: 'flex', flexDirection: 'column' } }}>
      {/* Header */}
      <Box sx={{ bgcolor: color, color: '#fff', px: 2.5, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {req.order_machine_identifier} · {req.order_machine_name}
            </Typography>
            <Typography variant="h6" fontWeight={800} lineHeight={1.25}>{req.order_product_name}</Typography>
            {req.order_product_article && (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Арт: {req.order_product_article}</Typography>
            )}
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff', mt: -0.5 }}><CloseIcon /></IconButton>
        </Stack>
        <Chip label={req.status_display} size="small" sx={{
          mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: 11,
        }} />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
        {/* Основна інфо */}
        <Typography variant="overline" color="text.secondary" fontWeight={700}>Замовлення</Typography>
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 2 }}>
          <InfoRow label="Прес-форма" value={req.order_press_form_identifier
            ? `${req.order_press_form_identifier}${req.order_press_form_name ? ' — ' + req.order_press_form_name : ''}`
            : null} />
          <InfoRow label="Кількість" value={req.order_quantity ? `${req.order_quantity} шт` : null} />
          <InfoRow label="Плановий старт" value={fmtDt(req.order_planned_start)} />
          {req.order_customer_order_number && (
            <InfoRow label="№ замовлення" value={req.order_customer_order_number} />
          )}
          {req.order_product_color && (
            <InfoRow label="Колір" value={req.order_product_color} />
          )}
          {req.order_comment && (
            <>
              <Divider sx={{ my: 0.75 }} />
              <Typography variant="caption" color="text.secondary">Коментар до замовлення:</Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>{req.order_comment}</Typography>
            </>
          )}
        </Paper>

        {/* Сировина */}
        {rawMaterials.length > 0 && (
          <>
            <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
              <ScienceIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="overline" color="text.secondary" fontWeight={700}>Сировина для замовлення</Typography>
            </Stack>
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 2 }}>
              {rawMaterials.map((m, i) => (
                <Stack key={i} direction="row" justifyContent="space-between" py={0.4}>
                  <Typography variant="caption">{m.name}</Typography>
                  <Typography variant="caption" fontWeight={700}>{m.quantity} кг</Typography>
                </Stack>
              ))}
            </Paper>
          </>
        )}

        {/* Замішування */}
        {mixing && (
          <>
            <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
              <InventoryIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="overline" color="text.secondary" fontWeight={700}>Заміс</Typography>
            </Stack>
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 2 }}>
              {mixing.batch_number && <InfoRow label="Номер замісу" value={mixing.batch_number} />}
              <InfoRow label="Готовність"
                value={mixing.is_ready === true ? 'Готовий ✓' : mixing.is_ready === false ? 'Не готовий' : '—'}
                color={mixing.is_ready ? '#2e7d32' : '#c62828'} />
              {mixing.note && <InfoRow label="Примітка" value={mixing.note} />}
            </Paper>
          </>
        )}

        {/* Наладка — хто і коли */}
        <Typography variant="overline" color="text.secondary" fontWeight={700}>Наладка</Typography>
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 2 }}>
          <InfoRow label="Заявка створена" value={fmtDt(req.created_at)} />
          {req.adjuster_name && <InfoRow label="Наладчик" value={req.adjuster_name} />}
          {req.started_at && <InfoRow label="Початок наладки" value={fmtDt(req.started_at)} />}
          {req.completed_at && <InfoRow label="Завершення" value={fmtDt(req.completed_at)} color="#2e7d32" />}
          {duration && <InfoRow label="Тривалість" value={duration} />}
          {req.actual_cycle_time_seconds != null && (
            <InfoRow label="Реальний час циклу" value={`${req.actual_cycle_time_seconds} сек`} color="#1565c0" />
          )}
          {Array.isArray(req.actual_product_weights) && req.actual_product_weights.length > 0 ? (
            req.actual_product_weights.map((w) => (
              <InfoRow
                key={w.product_id}
                label={`Вага: ${w.product_name || `виріб #${w.product_id}`}`}
                value={`${Math.round(Number(w.weight_kg) * 1000)} г`}
                color="#1565c0"
              />
            ))
          ) : req.actual_product_weight_kg != null ? (
            <InfoRow
              label="Реальна вага виробу"
              value={`${Math.round(Number(req.actual_product_weight_kg) * 1000)} г`}
              color="#1565c0"
            />
          ) : null}
        </Paper>

        {/* Затрати після завершення */}
        {(() => {
          const wasteEntry = Array.isArray(wastes) && wastes.length > 0 ? wastes[0] : null;
          const hasWaste = wasteEntry && (wasteEntry.total_kg > 0);
          if (!hasWaste && !req.comment) return null;
          return (
            <>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>Результат наладки</Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 2 }}>
                {hasWaste && (
                  <>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Відходи наладки:</Typography>
                    {wasteEntry.recoverable_kg > 0 && (
                      <Stack direction="row" justifyContent="space-between" py={0.3}>
                        <Typography variant="caption">Відновлювальні (дроблення)</Typography>
                        <Typography variant="caption" fontWeight={700}>{wasteEntry.recoverable_kg} кг</Typography>
                      </Stack>
                    )}
                    {wasteEntry.non_recoverable_kg > 0 && (
                      <Stack direction="row" justifyContent="space-between" py={0.3}>
                        <Typography variant="caption">Невідновлювальні (утилізація)</Typography>
                        <Typography variant="caption" fontWeight={700}>{wasteEntry.non_recoverable_kg} кг</Typography>
                      </Stack>
                    )}
                    {req.comment && <Divider sx={{ my: 0.75 }} />}
                  </>
                )}
                {req.comment && (
                  <>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Коментар:</Typography>
                    <Typography variant="body2">{req.comment}</Typography>
                  </>
                )}
              </Paper>
            </>
          );
        })()}
      </Box>

      {/* Footer actions */}
      {(req.status === 'pending' || req.status === 'in_progress') && (
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', flexShrink: 0 }}>
          {req.status === 'pending' && (
            <Button fullWidth variant="contained" size="large"
              startIcon={taking ? <CircularProgress size={16} color="inherit" /> : <BuildIcon />}
              onClick={() => onTake(req)} disabled={taking}
              sx={{ bgcolor: '#1565c0', borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
              Взяти в роботу
            </Button>
          )}
          {req.status === 'in_progress' && (
            <Button fullWidth variant="contained" size="large"
              startIcon={<CheckCircleIcon />}
              onClick={() => { onClose(); onComplete(req); }}
              sx={{ bgcolor: '#2e7d32', borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
              Завершити наладку
            </Button>
          )}
        </Box>
      )}
    </Drawer>
  );
}

function RequestCard({ req, onClick }) {
  const sc = {
    pending: { color: '#e65100', bg: '#fff3e0' },
    in_progress: { color: '#1565c0', bg: '#e3f2fd' },
    completed: { color: '#2e7d32', bg: '#e8f5e9' },
  }[req.status] || { color: '#555', bg: '#f5f5f5' };

  return (
    <Paper elevation={1} onClick={() => onClick(req)} sx={{
      borderRadius: 2.5, p: 1.75, mb: 1.5,
      border: `1px solid ${sc.color}22`,
      cursor: 'pointer',
      '&:hover': { boxShadow: 4, borderColor: sc.color },
      transition: 'box-shadow .15s, border-color .15s',
    }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.25 }}>
        {req.order_machine_identifier} · {req.order_machine_name}
      </Typography>
      <Typography variant="body2" fontWeight={800} lineHeight={1.3}>{req.order_product_name}</Typography>
      {req.order_press_form_identifier && (
        <Typography variant="caption" color="text.secondary">ПФ: {req.order_press_form_identifier}</Typography>
      )}
      <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
        <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, px: 1, py: 0.25 }}>
          <Typography variant="caption" fontWeight={700}>{req.order_quantity} шт</Typography>
        </Box>
        <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, px: 1, py: 0.25 }}>
          <Typography variant="caption">{fmtDt(req.order_planned_start)}</Typography>
        </Box>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
        <AccessTimeIcon sx={{ fontSize: 12 }} />{fmtDt(req.created_at)}
      </Typography>
      {req.status === 'in_progress' && req.adjuster_name && (
        <Typography variant="caption" color="#1565c0" fontWeight={700} sx={{ display: 'block', mt: 0.5 }}>
          👤 {req.adjuster_name}
        </Typography>
      )}
      {req.status === 'completed' && (
        <Typography variant="caption" color="#2e7d32" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <CheckCircleIcon sx={{ fontSize: 13 }} />{req.adjuster_name} · {fmtDt(req.completed_at)}
        </Typography>
      )}
    </Paper>
  );
}

function Column({ title, color, bg, count, children }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ bgcolor: bg, borderRadius: 2, px: 1.5, py: 1, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" fontWeight={800} color={color} sx={{ flex: 1 }}>{title}</Typography>
        <Chip label={count} size="small" sx={{ bgcolor: color, color: '#fff', fontWeight: 800, height: 20, fontSize: 11 }} />
      </Box>
      <Box sx={{ minHeight: 80 }}>{children}</Box>
    </Box>
  );
}

function CompleteDialog({ open, req, onClose, onDone }) {
  const [recoverableKg, setRecoverableKg] = useState('');
  const [nonRecoverableKg, setNonRecoverableKg] = useState('');
  const [cycleTime, setCycleTime] = useState('');
  const [weightByProduct, setWeightByProduct] = useState({});
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const kitProducts = Array.isArray(req?.kit_products) && req.kit_products.length
    ? req.kit_products
    : (req?.order_product_name
      ? [{
        product_id: 'primary',
        product_name: req.order_product_name,
        product_article: req.order_product_article || '',
        is_primary: true,
      }]
      : []);
  const isKit = kitProducts.length > 1;

  useEffect(() => {
    if (open) {
      setRecoverableKg('');
      setNonRecoverableKg('');
      setCycleTime('');
      const init = {};
      (Array.isArray(req?.kit_products) ? req.kit_products : []).forEach((p) => {
        init[p.product_id] = '';
      });
      if (!Object.keys(init).length) init.primary = '';
      setWeightByProduct(init);
      setComment('');
    }
  }, [open, req]);

  const handleSubmit = async () => {
    const cycle = parseFloat(cycleTime);
    if (!cycle || cycle <= 0) {
      alert('Вкажіть реальний час циклу (сек).');
      return;
    }
    const weightsPayload = [];
    for (const p of kitProducts) {
      const raw = weightByProduct[p.product_id];
      const g = parseFloat(raw);
      if (!g || g <= 0) {
        alert(`Вкажіть вагу (г) для «${p.product_name || 'виріб'}».`);
        return;
      }
      if (p.product_id !== 'primary' && Number.isFinite(Number(p.product_id))) {
        weightsPayload.push({ product_id: Number(p.product_id), weight_g: g });
      }
    }
    setLoading(true);
    try {
      const body = {
        comment: comment.trim(),
        recoverable_kg: parseFloat(recoverableKg) || 0,
        non_recoverable_kg: parseFloat(nonRecoverableKg) || 0,
        actual_cycle_time_seconds: cycle,
      };
      if (weightsPayload.length > 0) {
        body.actual_product_weights_g = weightsPayload;
      } else {
        // Один виріб без kit_products з API
        const only = kitProducts[0];
        body.actual_product_weight_g = parseFloat(weightByProduct[only?.product_id]);
      }
      await setupRequestService.complete(req.id, body);
      onDone();
    } catch (e) {
      const data = e?.response?.data;
      const msg = data?.actual_cycle_time_seconds
        || data?.actual_product_weights_g
        || data?.actual_product_weight_g
        || data?.actual_product_weight_kg
        || data?.detail
        || 'Помилка';
      alert(Array.isArray(msg) ? msg[0] : msg);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ bgcolor: '#2e7d32', px: 3, py: 2 }}>
        <Typography variant="h6" fontWeight={800} color="#fff">Завершити наладку</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          {req?.order_product_name} · {req?.order_machine_identifier}
          {isKit ? ` · комплект (${kitProducts.length} вир.)` : ''}
        </Typography>
      </Box>
      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              Параметри партії
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
              {isKit
                ? 'Для комплекту вкажіть вагу кожного виробу окремо (грами).'
                : 'Фіксуються разом із конфігурацією (ТПА, прес-форма, сировина, барвник, виріб)'}
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                label="Реальний час циклу (сек)" type="number" size="small" fullWidth required
                value={cycleTime} onChange={(e) => setCycleTime(e.target.value)}
                inputProps={{ min: 0.1, step: 0.1 }} placeholder="напр. 12.5"
              />
              {kitProducts.map((p) => {
                const labelName = p.product_name || `Виріб #${p.product_id}`;
                const art = p.product_article ? ` (${p.product_article})` : '';
                return (
                  <TextField
                    key={p.product_id}
                    label={`Вага: ${labelName}${art} (г)`}
                    type="number"
                    size="small"
                    fullWidth
                    required
                    value={weightByProduct[p.product_id] ?? ''}
                    onChange={(e) => setWeightByProduct((prev) => ({
                      ...prev,
                      [p.product_id]: e.target.value,
                    }))}
                    inputProps={{ min: 0.1, step: 1 }}
                    placeholder="напр. 14"
                    helperText={p.is_primary ? 'Головний виріб замовлення' : 'Супутній виріб комплекту'}
                  />
                );
              })}
            </Stack>
          </Box>
          <Divider />
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <RecyclingIcon sx={{ color: '#1565c0', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={700} color="#1565c0">
                Відновлювальні відходи
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Залишки замісу, придатні для дроблення → Склад дроблення
            </Typography>
            <TextField
              label="Кількість (кг)" type="number" size="small" fullWidth
              value={recoverableKg} onChange={(e) => setRecoverableKg(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }} placeholder="0"
            />
          </Box>
          <Divider />
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <DeleteForeverIcon sx={{ color: '#c62828', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={700} color="#c62828">
                Невідновлювальні відходи
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Непридатні відходи → списання на утилізацію
            </Typography>
            <TextField
              label="Кількість (кг)" type="number" size="small" fullWidth
              value={nonRecoverableKg} onChange={(e) => setNonRecoverableKg(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }} placeholder="0"
            />
          </Box>
          <Divider />
          <TextField label="Коментар / результат наладки" fullWidth multiline rows={2}
            value={comment} onChange={(e) => setComment(e.target.value)} size="small"
            placeholder="Що зроблено, які параметри встановлено, зауваження..." />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Скасувати</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}
          sx={{ bgcolor: '#2e7d32', borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
          {loading ? <CircularProgress size={18} /> : 'Підтвердити завершення'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SetupPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [takingId, setTakingId] = useState(null);
  const [detailReq, setDetailReq] = useState(null);
  const [completeDialog, setCompleteDialog] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser') || 'null'); }
    catch { return null; }
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await setupRequestService.getAll({ page_size: 200, ordering: '-created_at' });
      const data = r.data?.results || r.data || [];
      setRequests(data);
      // Refresh detail if open
      if (detailReq) {
        const fresh = data.find((x) => x.id === detailReq.id);
        if (fresh) setDetailReq(fresh);
      }
    } catch (_) {
    } finally { setLoading(false); }
  }, [detailReq?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const handleTake = useCallback(async (req) => {
    setTakingId(req.id);
    try {
      await setupRequestService.take(req.id, { adjuster_name: currentUser?.full_name || '' });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Помилка');
    } finally { setTakingId(null); }
  }, [currentUser?.full_name, load]);

  const filterReqs = (reqs) => {
    let filtered = reqs;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter((r) =>
        (r.order_machine_identifier || '').toLowerCase().includes(q) ||
        (r.order_machine_name || '').toLowerCase().includes(q) ||
        (r.order_product_name || '').toLowerCase().includes(q) ||
        (r.order_press_form_identifier || '').toLowerCase().includes(q) ||
        (r.adjuster_name || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  };

  const allFiltered = filterReqs(requests);
  const pending = allFiltered.filter((r) => r.status === 'pending');
  const inProgress = allFiltered.filter((r) => r.status === 'in_progress');
  const done = allFiltered.filter((r) => r.status === 'completed');

  const visibleCols = statusFilter === 'all'
    ? ['pending', 'in_progress', 'completed']
    : [statusFilter];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar: search + status filter + refresh */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} mb={2.5}>
        <TextField
          size="small"
          placeholder="Пошук по верстату, виробу, наладчику..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 380 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, v) => { if (v) setStatusFilter(v); }}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.5, fontSize: 12 } }}
        >
          <ToggleButton value="all">Всі</ToggleButton>
          <ToggleButton value="pending" sx={{ color: '#e65100', '&.Mui-selected': { bgcolor: '#fff3e0', color: '#e65100' } }}>Нові</ToggleButton>
          <ToggleButton value="in_progress" sx={{ color: '#1565c0', '&.Mui-selected': { bgcolor: '#e3f2fd', color: '#1565c0' } }}>В роботі</ToggleButton>
          <ToggleButton value="completed" sx={{ color: '#2e7d32', '&.Mui-selected': { bgcolor: '#e8f5e9', color: '#2e7d32' } }}>Наладжено</ToggleButton>
        </ToggleButtonGroup>
        <IconButton onClick={load} disabled={loading} size="small" sx={{ ml: 'auto' }}>
          {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
        </IconButton>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
        {visibleCols.includes('pending') && (
          <Column title="Нові" color="#e65100" bg="#fff3e0" count={pending.length}>
            {pending.length === 0
              ? <Typography variant="caption" color="text.secondary" fontStyle="italic">Немає нових заявок</Typography>
              : pending.map((req) => <RequestCard key={req.id} req={req} onClick={setDetailReq} />)
            }
          </Column>
        )}
        {visibleCols.includes('in_progress') && (
          <Column title="В роботі" color="#1565c0" bg="#e3f2fd" count={inProgress.length}>
            {inProgress.length === 0
              ? <Typography variant="caption" color="text.secondary" fontStyle="italic">Немає активних наладок</Typography>
              : inProgress.map((req) => <RequestCard key={req.id} req={req} onClick={setDetailReq} />)
            }
          </Column>
        )}
        {visibleCols.includes('completed') && (
          <Column title="Наладжено" color="#2e7d32" bg="#e8f5e9" count={done.length}>
            {done.length === 0
              ? <Typography variant="caption" color="text.secondary" fontStyle="italic">Немає завершених</Typography>
              : done.map((req) => <RequestCard key={req.id} req={req} onClick={setDetailReq} />)
            }
          </Column>
        )}
      </Stack>

      <DetailDrawer
        req={detailReq}
        taking={takingId === detailReq?.id}
        onClose={() => setDetailReq(null)}
        onTake={async (req) => { await handleTake(req); }}
        onComplete={(req) => setCompleteDialog(req)}
      />

      <CompleteDialog open={!!completeDialog} req={completeDialog}
        onClose={() => setCompleteDialog(null)}
        onDone={() => { setCompleteDialog(null); load(); }} />
    </Box>
  );
}
